import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { newsList } from '@/mocks/news';
import { newsCategoryOptions } from '@/mocks/filters';
import type { NewsItem } from '@/types';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const PAGE_SIZE = 5;

export default function NewsPage() {
  const [category, setCategory] = useState('all');
  const [page, setPage] = useState(1);

  useDocumentMeta({
    title: '影视资讯 - 行业动态与明星资讯 | 善良的女秘书',
    description:
      '善良的女秘书影视资讯频道，提供行业动态、明星资讯、剧透解析与影视评论等内容，按时间倒序更新，助你紧跟影视圈最新热点。',
    keywords: '影视资讯,行业动态,明星资讯,剧透解析,影视评论,善良的女秘书',
  });

  useEffect(() => {
    setPage(1);
  }, [category]);

  const filtered = useMemo(() => {
    const list = (newsList as NewsItem[])
      .filter((item) => category === 'all' || item.category === category)
      .sort((a, b) => (a.date < b.date ? 1 : -1));
    return list;
  }, [category]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const hotNews = [...(newsList as NewsItem[])]
    .sort((a, b) => b.views - a.views)
    .slice(0, 4);

  return (
    <div className="w-full px-4 py-8 md:px-8 lg:px-12">
      <nav className="mb-5 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="whitespace-nowrap transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line text-foreground-300"></i>
        <span className="whitespace-nowrap text-foreground-800">影视资讯</span>
      </nav>

      <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
        影视资讯
      </h1>
      <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-500">
        汇集行业动态、明星资讯、剧透解析与影视评论，按时间倒序更新，帮你快速掌握影视圈最新动向。
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-1.5 rounded-full bg-background-200 p-1">
        {newsCategoryOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setCategory(option.value)}
            className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
              category === option.value
                ? 'bg-background-50 font-medium text-primary-600'
                : 'text-foreground-600 hover:text-foreground-900'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_300px]">
        <div>
          {visible.length > 0 ? (
            <>
              <div className="flex flex-col gap-4">
                {visible.map((item) => (
                  <Link
                    key={item.id}
                    to={`/news/${item.id}`}
                    className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 sm:flex-row"
                  >
                    <div className="h-40 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-28 sm:w-44">
                      <img
                        src={item.cover}
                        alt={item.title}
                        title={`${item.title} 资讯详情`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex items-center gap-2 text-xs text-foreground-400">
                        <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-secondary-900">
                          {item.category}
                        </span>
                        <span>{item.date}</span>
                      </div>
                      <h2 className="mt-2 line-clamp-2 text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
                        {item.title}
                      </h2>
                      <p className="mt-1.5 line-clamp-2 text-sm text-foreground-500">
                        {item.summary}
                      </p>
                      <div className="mt-auto flex items-center gap-3 pt-3 text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-user-line"></i>
                          {item.author}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {item.views.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </>
          ) : (
            <div>
              <EmptyState
                title="该分类暂无资讯"
                description="换个分类看看，或浏览下方热门资讯。"
                actionLabel="查看全部资讯"
                onAction={() => setCategory('all')}
              />
              <div className="mt-6">
                <h3 className="mb-3 font-heading text-base font-semibold text-foreground-950">
                  热门资讯推荐
                </h3>
                <div className="flex flex-col gap-3">
                  {hotNews.map((item) => (
                    <Link
                      key={item.id}
                      to={`/news/${item.id}`}
                      className="line-clamp-1 text-sm text-foreground-700 transition-colors hover:text-primary-600"
                    >
                      {item.title}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <aside className="hidden lg:block">
          <div className="sticky top-20 overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <h3 className="border-b border-background-200 px-4 py-3 font-heading text-sm font-semibold text-foreground-950">
              热门阅读
            </h3>
            <div className="flex flex-col divide-y divide-background-200">
              {hotNews.map((item, index) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  className="group flex items-start gap-3 px-4 py-3 transition-colors hover:bg-background-100"
                >
                  <span className="font-heading text-lg font-bold text-background-300 group-hover:text-primary-400">
                    {index + 1}
                  </span>
                  <span className="line-clamp-2 text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                    {item.title}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}